import React, { useMemo } from 'react';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Bar, Cell } from 'recharts';
import { findQuestionIdsByTitles, findAnswersByQuestionIds } from '@/src/utils/findAnswersByTitles';
//types
import { RadarChartProps } from '@/src/types/components/RadarChart';

export default function RadarChart({
  formJson = null,
  answersJson = {},
  questionTitles,
  maxValue = 5,
  // color = '#8884d8',

}: RadarChartProps) {


    const possibleAnswers = [
    {
      text: 'Wettelijke basis',
      value: 0
    },
    {
      text: 'Minimaal',
      value: 1
    },
    {
      text: 'Enige ambitie',
      value: 2
    },
    {
      text: 'Maximaal',
      value: 3
    }
  ]

  // Use the utility functions to find question IDs and their answers
  const processedData = useMemo(() => {
    // Check if we're using the new approach with formJson and answersJson
    if (formJson && answersJson && Object.keys(answersJson).length > 0 && questionTitles) {
      // Step 1: Find question IDs by their titles
      const questionIds = findQuestionIdsByTitles(formJson, questionTitles);

      // Step 2: Find answers for those question IDs
      const answers = findAnswersByQuestionIds(answersJson, questionIds);

      // Step 3: Transform into chart data format
      return questionTitles && questionTitles.map(title => {
        // The utility should now preserve the original title case in the keys
        // But we'll still do a case-insensitive match as a fallback
        // let answer = answers[title];
        const answer = possibleAnswers.find(answer => answer.text == answers[title])?.value;

        return {
          subject: title.charAt(0).toUpperCase() + title.slice(1), // Capitalize first letter
          value: answer ? Number(answer) : 0,
          fullMark: maxValue
        };
      });
    }
    return [];
  }, [formJson, answersJson, questionTitles, maxValue, possibleAnswers]);

  const displayData = processedData.length > 0 ? processedData : null;

  const hexColors = [
    '#FF6384',
    '#36A2EB',
    '#FFCE56',
    '#4BC0C0',
    '#9966FF',
    '#FF9F40',
    '#C9CBCF',
    '#8DD1E1',
    '#FFB6B9',
    '#B5EAD7'
  ];



  return (
    <ResponsiveContainer width="100%" height={500}>
      {displayData ? (
        <BarChart
          data={displayData}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          barCategoryGap={32}
          barGap={8}
        >
          <XAxis
            type="number"
            domain={[0, possibleAnswers.length-1]}
            tickCount={possibleAnswers.length-1}
            allowDecimals={false}
            ticks={possibleAnswers.map(answer => answer.value)}
            tickFormatter={(value) => possibleAnswers.find(answer => answer.value === value)?.text || ''}
          />
          <YAxis 
            type="category" 
            dataKey="subject" 
            tick={{ style: { whiteSpace: 'nowrap' } }}
            width={200}
          />
          <Bar
            dataKey="value"
            radius={[16, 16, 16, 16]}
            barSize={24}
            minPointSize={4}
            width={100}
          >
            {displayData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={hexColors[index % hexColors.length]} />
            ))}
          </Bar>
        </BarChart>
      ) : (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">No data available</p>
        </div>
      )}
    </ResponsiveContainer>
  );
}